// ──────────────────────────── Feuille De Temps Status ────────────────────────────
// Maps the `statut: string | null` from the backend entity (FeuilleDeTempsStatut enum)
// to display-ready status configuration (canonical shape, plan §2.5.2).
//
// IMPORTANT: `code` must match the backend FeuilleDeTempsStatut enum values exactly
// (EN_ATTENTE, VALIDE, REJETE) — these are sent as query params to the API.
// `token` is an L2.5 semantic token (lib/config/statusTokens.ts) — StatusBadge
// resolves token → --status-<token> / --status-<token>-bg pairs.

import type { StatusToken } from "@lib/config/statusTokens";
import { CheckCircle, Clock, type LucideIcon, XCircle } from "lucide-react";

export interface StatusConfig {
  readonly code: string;
  readonly label: string;
  readonly token: StatusToken;
  readonly icon?: LucideIcon;
}

export const STATUT_FEUILLE = {
  VALIDEE: {
    code: "VALIDE",
    label: "Validée",
    token: "completed",
    icon: CheckCircle,
  },
  REJETEE: {
    code: "REJETE",
    label: "Rejetée",
    token: "rejected",
    icon: XCircle,
  },
  EN_ATTENTE: {
    code: "EN_ATTENTE",
    label: "En attente",
    token: "pending",
    icon: Clock,
  },
} as const satisfies Record<string, StatusConfig>;

export type FeuilleStatus = keyof typeof STATUT_FEUILLE;

/**
 * Maps the backend statut field to a FeuilleStatus.
 * - `'VALIDE'`   → VALIDEE
 * - `'REJETE'`   → REJETEE
 * - `'EN_ATTENTE'` or null/undefined → EN_ATTENTE
 */
export function resolveStatutFeuille(statut: string | null | undefined): FeuilleStatus {
  if (statut === "VALIDE") return "VALIDEE";
  if (statut === "REJETE") return "REJETEE";
  return "EN_ATTENTE";
}
